import { useEffect, useCallback, useRef } from 'react';
import { useStreakStore } from '../stores/streakStore';
import { fetchStreakFull, fetchStreakIncremental, toDateID, StreakResult } from '../services/streakService';

interface UseStreakDataResult {
  streakResult: StreakResult | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}
/**
 * Fetches and caches streak data for a user.
 *
 * Two-path logic (Option C):
 *
 *  COLD START / CACHE MISS
 *    → fetchStreakFull: walks backward in 30-day batches until a gap is found.
 *      Costs ceil(streak / 30) Firestore reads. Result + lastLoggedDateID stored.
 *
 *  WARM CACHE (within 1-hour TTL)
 *    → fetchStreakIncremental: 2-doc read (today + yesterday).
 *      If today IS logged → streak + 1 (or same if already counted).
 *      If today NOT logged + yesterday IS logged → streak unchanged.
 *      If neither → falls through to fetchStreakFull.
 *
 *  CACHE INVALIDATED (after log submit, debug clear, etc.)
 *    → cache entry deleted → Map ref changes → useEffect re-fires → fetchStreakFull.
 *
 * Re-fetches automatically whenever the cache entry for this userId
 * is cleared (e.g. after a new log is submitted).
 */
export function useStreakData(userId: string | undefined): UseStreakDataResult {
  // Subscribe to store ONLY for the values we return (prevents re-renders from other users' data)
  const store = useStreakStore();

  const streakResult = userId ? store.getStreakResult(userId) : null;
  const loading      = userId ? store.isLoading(userId) : false;
  const error        = userId ? store.getError(userId) : null;

  // Prevent concurrent fetches
  const fetchInProgressRef = useRef(false);

  const fetchData = useCallback(async () => {
    if (!userId) return;
    if (fetchInProgressRef.current) return;

    // Use getState() — gets current value without subscribing, avoids dependency loops
    const {
      isCacheValid,
      getStreakResult,
      getLastLoggedDateID,
      setStreakData,
      setLoading,
      setError,
    } = useStreakStore.getState();

    // ── Warm path: cache is fresh, do a cheap incremental check ──────────────
    if (isCacheValid(userId)) {
      const cached = getStreakResult(userId);
      const lastLoggedDateID = getLastLoggedDateID(userId);

      const todayID = toDateID(new Date());
      if (cached && lastLoggedDateID) {
        if (lastLoggedDateID === todayID && cached.daysSinceLastLog === 0) {
          return;
        }

        try {
          const result = await fetchStreakIncremental(
            userId,
            cached.currentStreak,
            lastLoggedDateID,
          );
          const newLastLoggedDateID =
            result.daysSinceLastLog === 0
              ? todayID
              : lastLoggedDateID;
          useStreakStore.getState().setStreakData(userId, result, newLastLoggedDateID);
          // console.log(
          //   `[useStreakData] Incremental — status: ${result.status}, streak: ${result.currentStreak}`,
          // );
          return;
        } catch (err) {
          console.warn('[useStreakData] Incremental check failed, falling back to full:', err);
        }
      }
    }

    // ── Cold path: full backward scan ────────────────────────────────────────
    // console.log(`[useStreakData] Full streak fetch for ${userId}`);
    fetchInProgressRef.current = true;
    useStreakStore.getState().setLoading(userId, true);

    try {
      const result = await fetchStreakFull(userId);
      const today  = new Date();
      const lastDate = new Date(today);
      lastDate.setDate(today.getDate() - result.daysSinceLastLog);
      lastDate.setHours(0, 0, 0, 0);
      const lastLoggedDateID = result.daysSinceLastLog < 10
        ? toDateID(lastDate)
        : '';
      useStreakStore.getState().setStreakData(userId, result, lastLoggedDateID);
      // console.log(
      //   `[useStreakData] Full fetch done — status: ${result.status}, streak: ${result.currentStreak}`,
      // );
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to fetch streak data';
      console.error(`[useStreakData] Error:`, msg);
      useStreakStore.getState().setError(userId, msg);
    } finally {
      fetchInProgressRef.current = false;
    }
  }, [userId]); // ✅ Only stable primitive values

  const refetch = useCallback(async () => {
    if (!userId) return;
    useStreakStore.getState().clearCache(userId);
    fetchInProgressRef.current = false; // Allow re-fetch
    await fetchData();
  }, [userId, fetchData]);

  // Only runs when userId changes
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { streakResult, loading, error, refetch };
}